import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  LayoutDashboard, FileText, UserCircle,
  ChevronLeft, ChevronRight, Sun, Moon,
  Calculator, Menu, X
} from 'lucide-react'

import ertcLogo from '../assets/ertcLogo.png'
import { TURNKEY_PROCESSOR_ID } from '../utilities/processorIds'

export default function Navbar({ applicationId, processorId }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [isExpanded, setIsExpanded] = useState(true)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [isDarkMode, setIsDarkMode] = useState(document.documentElement.classList.contains('dark'))

  // Always close mobile menu when navigating
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  const toggleDarkMode = () => {
    if (isDarkMode) {
      document.documentElement.classList.remove('dark')
      localStorage.theme = 'light'
      setIsDarkMode(false)
    } else {
      document.documentElement.classList.add('dark')
      localStorage.theme = 'dark'
      setIsDarkMode(true)
    }
  }

  const handleNavClick = (path) => {
    navigate(path)
  }

  const renderNavItem = (label, path, Icon, isMobile = false) => {
    // Simple path-matching for active state
    const isActive = location.pathname.startsWith(path) && (path !== '/dashboard' || location.pathname === '/dashboard')

    return (
      <button
        key={path}
        onClick={() => handleNavClick(path)}
        className={`relative flex items-center gap-3 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200 active:scale-95 ${(!isExpanded && !isMobile) && 'justify-center'} ${isActive
          ? 'bg-ertc-primary text-white shadow-md'
          : 'text-white/70 hover:bg-white/10 hover:text-white'
          }`}
        title={(!isExpanded && !isMobile) ? label : undefined}
      >
        <Icon size={20} className={isActive ? 'text-white' : 'text-white/70'} strokeWidth={isActive ? 2.5 : 2} />
        {(isExpanded || isMobile) && <span className='whitespace-nowrap'>{label}</span>}
      </button>
    )
  }

  const navLinks = (isMobile) => (
    <>
      <div className='flex flex-col gap-1 px-3'>
        {applicationId && (
          <>
            {renderNavItem('Dashboard', `/applications/${applicationId}/dashboard`, LayoutDashboard, isMobile)}
            {renderNavItem('Documents', `/applications/${applicationId}/documents`, FileText, isMobile)}

            {processorId === TURNKEY_PROCESSOR_ID && (
              renderNavItem('Quarter Breakdown', `/applications/${applicationId}/quarter-breakdown`, Calculator, isMobile)
            )}
          </>
        )}

        {/* If viewing the root dashboard (no active application) */}
        {!applicationId && (
          renderNavItem('Dashboard', `/dashboard`, LayoutDashboard, isMobile)
        )}
      </div>

      <div className='mt-auto pt-4'>
        {(isExpanded || isMobile) ? (
          <p className='px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-white/30'>
            Settings
          </p>
        ) : (
          <div className='mx-3 mb-2 border-t border-white/10' />
        )}
        <div className='flex flex-col gap-0.5 px-3'>
          {renderNavItem('Account', '/account', UserCircle, isMobile)}
        </div>
      </div>

      {(!isExpanded && !isMobile) ? (
        <div className='py-6 mt-4 border-t border-white/10 flex flex-col items-center justify-center gap-6 w-full'>
          <button
            onClick={toggleDarkMode}
            className='relative group flex items-center justify-center w-10 h-10 rounded-xl text-white/70 hover:bg-white/10 hover:text-white transition-all duration-200 active:scale-95'
          >
            {isDarkMode ? <Moon size={20} /> : <Sun size={20} />}
            <span className='absolute left-12 bg-gray-900 text-white text-xs font-medium px-2.5 py-1.5 rounded-md opacity-0 invisible group-hover:visible group-hover:opacity-100 transition-all duration-200 group-hover:delay-300 whitespace-nowrap shadow-xl border border-gray-700/50 z-50 pointer-events-none'>
              {isDarkMode ? 'Light Mode' : 'Dark Mode'}
            </span>
          </button>
        </div>
      ) : (
        <div className='p-4 border-t border-white/10 mt-4 w-full'>
          <button
            onClick={toggleDarkMode}
            className='w-full flex items-center gap-3 py-2.5 px-3 rounded-xl text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white transition-all duration-200 active:scale-95'
          >
            {isDarkMode ? <Moon size={20} /> : <Sun size={20} />}
            <span className='whitespace-nowrap'>{isDarkMode ? 'Dark Mode' : 'Light Mode'}</span>
          </button>
        </div>
      )}
    </>
  )

  return (
    <>
      {/* ─── MOBILE NAVBAR ─── */}
      <div className='md:hidden flex items-center justify-between px-4 py-3 bg-ertc-secondary shrink-0 shadow-md relative z-50'>
        <img onClick={() => navigate('/dashboard')} className='h-7 drop-shadow-sm cursor-pointer' src={ertcLogo} alt='ERTC Logo' />
        <button onClick={() => setMobileOpen(!mobileOpen)} className='text-white/70 hover:text-white hover:bg-white/10 p-1.5 rounded-lg transition-colors'>
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* MOBILE MENU OVERLAY */}
      {mobileOpen && (
        <div className='md:hidden fixed inset-0 top-[52px] bg-ertc-secondary z-40 flex flex-col overflow-y-auto pb-6 pt-4 animate-in fade-in slide-in-from-top-4 duration-300'>
          <nav className='flex flex-col flex-1'>
            {navLinks(true)}
          </nav>
        </div>
      )}

      {/* ─── DESKTOP SIDEBAR ─── */}
      <div
        className={`hidden md:flex h-screen bg-ertc-secondary border-r border-ertc-secondary/10 flex-col shrink-0 transition-all duration-300 ease-in-out shadow-lg z-10 ${isExpanded ? 'w-64' : 'w-20'}`}
      >
        <div className={`flex items-center py-4 ${isExpanded ? 'px-6 justify-between' : 'px-0 justify-center'}`}>
          {isExpanded && <img onClick={() => navigate('/dashboard')} className='w-32 drop-shadow-sm cursor-pointer' src={ertcLogo} alt='ERTC Logo' />}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className='text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors p-1.5'
            aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {isExpanded ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
          </button>
        </div>

        <nav className='flex flex-col mt-2 flex-1 gap-1'>
          {navLinks(false)}
        </nav>
      </div>
    </>
  )
}